import styled from '@emotion/styled'
import React, { useContext, useEffect, useRef } from 'react'
import {
    EDITOR_BANNER_BLOCK_IDX,
    EDITOR_FLOATING_BUTTON_BLOCK_IDX,
    isCartActionButton,
} from '@storefronts/common-types'
import { ContentBlockContext } from '../../../../context/contentBlockContext'
import {
    HEADER_BORDER_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_BORDER_COLOR_VAR,
} from '../../../../context/CustomizationContextProvider'
import { OverrideContext } from '../../../../context/overrideContext'
import { PageContext } from '../../../../context/PageContextProvider'
import { QueryParamContext } from '../../../../context/QueryParamContextProvider'
import { ViddyLPContext } from '../../../../context/viddyLPContext'
import { useHeaderScrollVisibility } from '../../../../hooks/useHeaderScrollVisibility'
import { useScrollPast } from '../../../../hooks/useScrollPast'
import {
    HEADER_HEIGHT_VAR,
    PAGE_WIDTH_VAR,
    ScrollingLayout,
} from '../../../../style/scrollingLayout'
import {
    getStickyFooterSafeAreaPadding,
    STICKY_FOOTER_SHADOW,
} from '../../../../style/styleConstants'
import { Banner } from '../../../shared/Banner'
import { LPMainButton } from '../../../shared/CTAButton'
import { EditorSectionHighlight } from '../../../shared/EditorSectionHighlight'
import { PoweredByFooter } from '../../../shared/PoweredByFooter'
import { AllProductsHeader } from '../../AllProductsHeader'
import { FloatingCart } from '../../FloatingCart'
import { TabContainer } from '../../TabContainer'
import { HeroVideoBlock } from './HeroVideoBlock'

const MOBILE_SCROLL_THRESHOLD = 500

const PageSurface = styled.div({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100%',
    // The fill runs edge to edge; the centred column below sits on top of it,
    // so a wide viewport reads as one page rather than a column on a backdrop.
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    // The hidden footer translates past the page's end; clipping keeps that
    // from growing the document. `clip` rather than `hidden` so this never
    // becomes the sticky children's scroll container.
    overflowY: 'clip',
})

/**
 * Transparent: `AllProductsHeader` and `Banner` each paint their own
 * background, so a fill here would double up.
 */
const HeaderBand = styled.div<{ isHidden: boolean }>(({ isHidden }) => ({
    position: 'sticky',
    top: 0,
    // Below the breakpoint the header follows the hero and rides up over it,
    // so a phone opens on video alone. Above it, the header is page chrome and
    // comes first. Ordering rather than duplicate markup keeps one header.
    order: 2,
    zIndex: 2,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    background: 'transparent',
    willChange: 'transform',
    transition: 'transform 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    transform: isHidden ? 'translateY(-100%)' : 'translateY(0)',
    // The header is always available on desktop: there is plenty of room for
    // it, and hiding chrome on a large screen costs more than it saves.
    [ScrollingLayout.DESKTOP_MEDIA]: {
        order: 1,
        transform: 'none',
        transition: 'none',
        willChange: 'auto',
    },
}))

/**
 * Caps its child at the content width on wide viewports and does nothing
 * below that. Adding padding here would double up with the padding the header
 * row, CTA and sections already carry.
 */
const CappedRow = styled.div<{ maxWidth: number }>(({ maxWidth }) => ({
    width: '100%',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        maxWidth,
        marginInline: 'auto',
    },
}))

const Main = styled.main({
    flex: 1,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
})

/**
 * Once the footer carries the product as well as the button it reads as its
 * own object, so on wide screens it lifts off the page edge as a card.
 */
const FLOATING_FOOTER_RADIUS_PX = 18
const FLOATING_FOOTER_INSET_PX = 16

/**
 * Lays the CTA out as a flex child because `LPMainButton` sets `width: 100%`
 * alongside a 20px margin, which only shrinks to fit inside a flex parent — in
 * a block parent it overflows by the margins.
 */
const FooterInner = styled.div<{ floating: boolean }>(({ floating }) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    [ScrollingLayout.DESKTOP_MEDIA]: {
        maxWidth: ScrollingLayout.CTA_MAX_WIDTH,
        marginInline: 'auto',
        ...(floating
            ? {
                  borderRadius: FLOATING_FOOTER_RADIUS_PX,
                  overflow: 'hidden',
                  background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
              }
            : null),
    },
}))

/**
 * Runs the header's fill and hairline the full width of the page while its
 * contents stay capped. AllProductsHeader paints both itself, so capping it
 * alone would stop the bar mid-page and let content scroll past on either side.
 * The inset shadow lands on the same pixel row as that component's own border,
 * so the two read as one continuous line.
 */
const HeaderRow = styled.div({
    width: '100%',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    boxShadow: `inset 0 -1px 0 var(${HEADER_BORDER_COLOR_VAR})`,
})

/**
 * Below the breakpoint the hero sticks and the content scrolls over it. Sticky
 * lives here rather than inside the hero so it has the whole of `Main` to
 * travel through. Above the breakpoint the hero is just the first thing on the
 * page.
 */
const HeroSlot = styled(CappedRow)({
    position: 'sticky',
    top: 0,
    order: 1,
    zIndex: 0,
    [ScrollingLayout.DESKTOP_MEDIA]: {
        position: 'static',
        order: 2,
        paddingInline: ScrollingLayout.PAGE_GUTTER_DESKTOP_PX,
        boxSizing: 'border-box',
    },
})

/** Rides over the sticky hero, so it needs its own stacking context and fill. */
const ContentOverHero = styled.div({
    position: 'relative',
    order: 3,
    zIndex: 1,
    width: '100%',
    backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
})

/**
 * Holds the page content width. Each section caps itself below that until it
 * has been migrated, so a migrated section can use the full content width
 * while its unmigrated neighbours stay at the width they were designed for.
 * Desktop-ready HTML bleeds to the page so authored backgrounds can reach
 * the edges; the fragment holds copy and grids to this column.
 *
 * No padding here: sections carry their own, and adding more would double up.
 */
const SectionColumn = styled.div({
    width: '100%',
    maxWidth: ScrollingLayout.CONTENT_MAX_WIDTH,
    marginInline: 'auto',
    marginBottom: ScrollingLayout.PAGE_GUTTER_PX,
})

const FOOTER_BASE_TRANSITION =
    'opacity 400ms ease-out, transform 400ms ease-out'
const footerTransition = (visible: boolean) =>
    `${FOOTER_BASE_TRANSITION}, visibility 0s linear ${visible ? '0s' : '400ms'}`

const FooterBar = styled.div<{
    mobileVisible: boolean
    isHidden: boolean
    hasBackground: boolean
}>(({ mobileVisible, isHidden, hasBackground }) => ({
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    height: hasBackground ? 'auto' : 96,
    background: hasBackground
        ? `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`
        : 'transparent',
    borderTop: hasBackground
        ? `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`
        : 'none',
    boxShadow: hasBackground ? STICKY_FOOTER_SHADOW : 'none',
    transition: footerTransition(mobileVisible && !isHidden),
    opacity: mobileVisible && !isHidden ? 1 : 0,
    visibility: mobileVisible && !isHidden ? 'visible' : 'hidden',
    transform: isHidden
        ? 'translateY(100%)'
        : mobileVisible
          ? 'translateY(0)'
          : 'translateY(20px)',
    pointerEvents: mobileVisible && !isHidden ? 'auto' : 'none',
    // Desktop shows the CTA without waiting for a scroll: the fold is much
    // further down and the button would otherwise be unreachable at the top.
    [ScrollingLayout.DESKTOP_MEDIA]: {
        transition: footerTransition(!isHidden),
        opacity: isHidden ? 0 : 1,
        visibility: isHidden ? 'hidden' : 'visible',
        transform: isHidden ? 'translateY(100%)' : 'translateY(0)',
        pointerEvents: isHidden ? 'none' : 'auto',
        // The card inside carries the fill and the edge, so the bar itself
        // steps back to being a positioning shell.
        ...(hasBackground
            ? {
                  background: 'transparent',
                  borderTop: 'none',
                  boxShadow: 'none',
                  paddingBottom: FLOATING_FOOTER_INSET_PX,
              }
            : null),
    },
}))

export const ScrollingContentBlock: React.FC = () => {
    const { landingPage } = useContext(ViddyLPContext)
    const { HIDE_CART_OVERRIDE } = useContext(QueryParamContext)
    const {
        mainContentOverride,
        displayPoweredByFooter = true,
        selectedBlockIdx,
    } = useContext(OverrideContext)
    const { hasVisibleCartActionButtons } = useContext(PageContext)

    const contentReviewsRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const surfaceRef = useRef<HTMLDivElement>(null)

    // Publish the header's height so the pinned hero video can sit below it,
    // and the page's content width so a section can bleed its background to
    // the page edges. Both are measured: the header is taller when the
    // storefront has a banner, and the page width excludes the scrollbar,
    // which `100vw` would wrongly include.
    useEffect(() => {
        const header = headerRef.current
        const surface = surfaceRef.current
        if (!header || !surface) return

        const publish = () => {
            surface.style.setProperty(
                HEADER_HEIGHT_VAR,
                `${header.offsetHeight}px`,
            )
            // On the document, not the surface: the overlays render outside
            // the page's subtree, and a section bleeding inside one of them
            // has to read the same page width the page itself reads.
            document.documentElement.style.setProperty(
                PAGE_WIDTH_VAR,
                `${document.documentElement.clientWidth}px`,
            )
        }

        publish()
        const observer = new ResizeObserver(publish)
        observer.observe(header)
        observer.observe(document.documentElement)
        return () => {
            observer.disconnect()
            // Owned by the block and set on `<html>` so overlays outside the
            // page's subtree can read it; take it back when the block goes.
            document.documentElement.style.removeProperty(PAGE_WIDTH_VAR)
        }
    }, [])

    const mobileVisible = useScrollPast(MOBILE_SCROLL_THRESHOLD)
    const isHeaderVisible = useHeaderScrollVisibility(undefined, headerRef)

    const isCartAction = isCartActionButton(landingPage.button)
    const isEditingFloatingButton =
        selectedBlockIdx === EDITOR_FLOATING_BUTTON_BLOCK_IDX
    const isEditingBanner = selectedBlockIdx === EDITOR_BANNER_BLOCK_IDX
    const shouldHideLPMainButton =
        isCartAction && hasVisibleCartActionButtons && !isEditingFloatingButton

    useEffect(() => {
        if (selectedBlockIdx !== EDITOR_BANNER_BLOCK_IDX) return
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }, [selectedBlockIdx])

    useEffect(() => {
        if (selectedBlockIdx !== EDITOR_FLOATING_BUTTON_BLOCK_IDX) return
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth',
        })
    }, [selectedBlockIdx])

    return (
        <ContentBlockContext.Provider value={{ contentReviewsRef }}>
            <PageSurface ref={surfaceRef}>
                {!HIDE_CART_OVERRIDE && (
                    <FloatingCart isVisible={!isHeaderVisible} />
                )}

                <Main>
                    <HeaderBand ref={headerRef} isHidden={!isHeaderVisible}>
                        <div
                            style={
                                isEditingBanner
                                    ? { position: 'relative', width: '100%' }
                                    : undefined
                            }
                        >
                            <Banner
                                bannerText={landingPage.bannerText}
                                bannerBenefits={landingPage.bannerBenefits}
                                brandBenefits={
                                    landingPage.settings?.brandBenefits
                                }
                            />
                            {isEditingBanner && <EditorSectionHighlight />}
                        </div>
                        <HeaderRow>
                            <CappedRow
                                maxWidth={ScrollingLayout.CONTENT_MAX_WIDTH}
                            >
                                <AllProductsHeader
                                    hideCartIcon={HIDE_CART_OVERRIDE}
                                    landingPage={landingPage}
                                />
                            </CappedRow>
                        </HeaderRow>
                    </HeaderBand>

                    <HeroSlot maxWidth={ScrollingLayout.CONTENT_MAX_WIDTH}>
                        <HeroVideoBlock />
                    </HeroSlot>
                    <ContentOverHero>
                        <SectionColumn>
                            {landingPage.bodyContent && (
                                <TabContainer
                                    content={
                                        mainContentOverride ||
                                        landingPage.bodyContent
                                    }
                                />
                            )}
                        </SectionColumn>
                        <PoweredByFooter
                            displayPoweredByFooter={displayPoweredByFooter}
                        />
                    </ContentOverHero>
                </Main>

                {landingPage.button && landingPage.button.type !== 'NONE' && (
                    <FooterBar
                        mobileVisible={mobileVisible || isEditingFloatingButton}
                        isHidden={shouldHideLPMainButton}
                        hasBackground={isCartAction}
                        style={
                            isCartAction
                                ? undefined
                                : {
                                      paddingBottom:
                                          getStickyFooterSafeAreaPadding(false),
                                  }
                        }
                    >
                        <FooterInner floating={isCartAction}>
                            <LPMainButton height={48} />
                        </FooterInner>
                        {isEditingFloatingButton && <EditorSectionHighlight />}
                    </FooterBar>
                )}
            </PageSurface>
        </ContentBlockContext.Provider>
    )
}
